import LanguageRounded from '@mui/icons-material/LanguageRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import ReceiptRounded from '@mui/icons-material/ReceiptRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import ShoppingCartCheckoutRounded from '@mui/icons-material/ShoppingCartCheckoutRounded';
import TagRounded from '@mui/icons-material/TagRounded';
import BackupRounded from '@mui/icons-material/BackupRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../../../auth/AuthProvider';
import { PageHeader } from '../../../components/states';
import { errorMessage } from '../../../lib/notify';
import { useSettings } from '../api';
import { NoAccess } from '../ui';
import { BackupTab } from './BackupTab';
import { BrandingTab } from './BrandingTab';
import { NumberingTab } from './NumberingTab';
import { PoliciesTab } from './PoliciesTab';
import { PosTab } from './PosTab';
import { PrintTab } from './PrintTab';
import { RegionalTab } from './RegionalTab';
import { SupportTab } from './SupportTab';
import { TaxTab } from './TaxTab';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useWhatsappStatus } from '../../whatsapp/api';
import { WhatsappTab } from '../../whatsapp/WhatsappTab';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useEmailStatus } from '../../email/api';
import { EmailTab } from '../../email/EmailTab';
import { PoweredBy } from '../../../components/PoweredBy';

export const SETTINGS_TABS = [
  {
    id: 'profile',
    label: 'Shop profile',
    hint: 'Name, contact & brand colour',
    icon: StorefrontRounded,
  },
  {
    id: 'regional',
    label: 'Regional',
    hint: 'Currency, language & time zone',
    icon: LanguageRounded,
  },
  { id: 'tax', label: 'Tax', hint: 'Tax rules and rates', icon: ReceiptRounded },
  { id: 'policies', label: 'Sales & stock', hint: 'Stock, payments & credit', icon: PolicyRounded },
  { id: 'printing', label: 'Printing', hint: 'Paper, footer & terms', icon: PrintRounded },
  { id: 'numbering', label: 'Numbering', hint: 'Document number formats', icon: TagRounded },
  {
    id: 'pos',
    label: 'Point of Sale',
    hint: 'Register, payments & receipt',
    icon: ShoppingCartCheckoutRounded,
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    hint: 'Link the shop’s number',
    icon: WhatsAppIcon,
  },
  {
    id: 'email',
    label: 'Email',
    hint: 'Mail servers & messages',
    icon: MailOutlineRounded,
  },
  {
    id: 'support',
    label: 'Support access',
    hint: 'Let support view your shop',
    icon: SupportAgentRounded,
  },
  { id: 'backup', label: 'Backup', hint: 'Download the whole shop', icon: BackupRounded },
] as const;
type TabId = (typeof SETTINGS_TABS)[number]['id'];
/** Tabs shown only to users who can manage settings. */
const MANAGE_ONLY_TABS: readonly TabId[] = ['support'];

export function SettingsPage() {
  const { can, me } = useAuth();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const { data: settings, isLoading, error, refetch } = useSettings();
  const canManage = can('settings.manage');
  // The WhatsApp and Email tabs exist only once the plugin is enabled for the shop.
  const whatsapp = useWhatsappStatus().data?.plugin.state;
  const email = useEmailStatus().data?.plugin.state;
  const enabled = (state: string | undefined) => state !== undefined && state !== 'NOT_ENABLED';
  const tabs = SETTINGS_TABS.filter(
    (t) =>
      (canManage || !MANAGE_ONLY_TABS.includes(t.id)) &&
      // Only the owner can download the whole shop.
      (t.id !== 'backup' || me?.isOwner === true) &&
      (t.id !== 'whatsapp' || enabled(whatsapp)) &&
      (t.id !== 'email' || (enabled(email) && can('email.manage'))),
  );
  const tab: TabId = tabs.some((t) => t.id === requested) ? (requested as TabId) : 'profile';
  const select = (id: TabId) => setParams(id === 'profile' ? {} : { tab: id });

  if (!can('settings.view')) {
    return (
      <>
        <PageHeader title="Settings" />
        <NoAccess what="shop settings" />
      </>
    );
  }

  let body: ReactNode;
  if (isLoading || (!settings && !error)) body = <SettingsSkeleton />;
  else if (!settings)
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        {errorMessage(error)}
      </Alert>
    );
  else
    body = {
      profile: <BrandingTab settings={settings} canManage={canManage} />,
      regional: <RegionalTab settings={settings} canManage={canManage} />,
      tax: <TaxTab settings={settings} canManage={canManage} />,
      policies: <PoliciesTab settings={settings} canManage={canManage} />,
      printing: <PrintTab settings={settings} canManage={canManage} />,
      numbering: <NumberingTab canManage={canManage} />,
      pos: <PosTab settings={settings} canManage={canManage} />,
      support: <SupportTab />,
      backup: <BackupTab />,
      whatsapp: <WhatsappTab />,
      email: <EmailTab />,
    }[tab];

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Make the app yours — branding, money, tax and the paperwork your shop prints."
      />
      {!canManage && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          You can view these settings. Changing them needs the “Manage settings” permission.
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '260px minmax(0,1fr)' },
          alignItems: 'start',
        }}
      >
        {wide ? (
          <Card sx={{ p: 1, position: 'sticky', top: 92 }} component="nav" aria-label="Settings">
            {tabs.map((t) => {
              const active = t.id === tab;
              const Icon = t.icon;
              return (
                <ButtonBase
                  key={t.id}
                  onClick={() => select(t.id)}
                  aria-current={active ? 'page' : undefined}
                  sx={(th) => {
                    const p = (th.vars ?? th).palette.primary.main;
                    return {
                      width: '100%',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: '10px',
                      bgcolor: active ? `color-mix(in srgb, ${p} 9%, transparent)` : 'transparent',
                      transition: 'background-color 140ms',
                      '&:hover': { bgcolor: active ? undefined : 'action.hover' },
                    };
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: active ? 'primary.main' : 'text.secondary' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: active ? 700 : 600,
                        color: active ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {t.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {t.hint}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Card>
        ) : (
          <Tabs
            value={tab}
            onChange={(_, v: TabId) => select(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 44 }}
          >
            {tabs.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>
        )}
        <Box
          key={tab}
          sx={{
            minWidth: 0,
            animation: 'sm-tab-in 200ms cubic-bezier(0.2,0,0,1)',
            '@keyframes sm-tab-in': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          {body}
        </Box>
      </Box>
      <PoweredBy sx={{ mt: 4 }} />
    </>
  );
}

function SettingsSkeleton() {
  return (
    <Stack spacing={2.5}>
      {[220, 160].map((h) => (
        <Card key={h} sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.75} sx={{ mb: 2.5 }}>
            <Skeleton variant="rounded" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="30%" height={24} />
              <Skeleton width="55%" />
            </Box>
          </Stack>
          <Skeleton variant="rounded" height={h} />
        </Card>
      ))}
    </Stack>
  );
}
